/**
 * Converte un valore in minuti decimali in una stringa "X minutes and Y seconds".
 *
 * @param {number} minutesFloat - Valore in minuti con parte decimale (es. 2.5 = 2 min 30 sec). Deve essere >= 0.
 * @returns {string} Stringa formattata (es. "2 minutes and 30 seconds")
 */
export const convertMinutesToMinutesAndSeconds = (minutesFloat) => {
  if (typeof minutesFloat !== 'number' || !isFinite(minutesFloat) || minutesFloat < 0) {
    throw new Error('convertMinutesToMinutesAndSeconds: expected finite non-negative number, got ' + typeof minutesFloat + ' ' + String(minutesFloat))
  }

  let minutes = Math.floor(minutesFloat)
  const decimal = minutesFloat - minutes
  let seconds = Math.round(decimal * 60)

  // Math.round può portare i secondi a 60 quando la parte decimale è molto vicina a 1
  // (es. 1.999 -> 0.999 * 60 = 59.94 -> round = 60). In questo caso normalizziamo
  // incrementando i minuti e azzerando i secondi.
  if (seconds === 60) {
    minutes += 1
    seconds = 0
  }

  return minutes + ' minutes and ' + seconds + ' seconds'
}

/**
 * Calcola date_start e date_end per un mese/anno dati.
 * Usa new Date(year, month, 0) per ottenere l'ultimo giorno del mese:
 * il giorno 0 del mese successivo corrisponde all'ultimo giorno del mese corrente.
 *
 * @param {number} month - Mese nel range 1-12
 * @param {number} year  - Anno a quattro cifre (es. 2026)
 * @returns {{ date_start: string, date_end: string }} Range nel formato YYYY-MM-DD
 */
export const getMonthDateRange = (month, year) => {
  if (typeof month !== 'number' || month < 1 || month > 12 || !Number.isInteger(month)) {
    throw new Error('getMonthDateRange: month must be an integer in range 1-12, got ' + String(month))
  }
  if (typeof year !== 'number' || !Number.isInteger(year) || year < 2000 || year > 2100) {
    throw new Error('getMonthDateRange: year must be an integer in range 2000-2100, got ' + String(year))
  }

  const paddedMonth = String(month).padStart(2, '0')
  const lastDay = new Date(year, month, 0).getDate()
  return {
    date_start: year + '-' + paddedMonth + '-01',
    date_end: year + '-' + paddedMonth + '-' + String(lastDay).padStart(2, '0'),
  }
}


/**
 * Calcola il "mese successivo" rispetto all'ultimo super cutoff registrato.
 *
 * Filtra l'array `cutoff_pm` mantenendo solo i record con `is_super_cutoff` truthy
 * (boolean true o numerico 1) e `is_valid` non esplicitamente falsy. Identifica la
 * `to_date` massima, somma un giorno e ritorna il range del mese cosi ottenuto.
 *
 * @param {Array<Object>} cutoffPm - Array dei record cutoff_pm restituiti da GET /pm/cutoffs/.
 * @returns {{ month: number, year: number, date_start: string, date_end: string } | null}
 *          Periodo del mese successivo, oppure null se l'array e mancante, vuoto, non valido
 *          o non contiene super cutoff utilizzabili.
 */
export const getNextSuperCutoffPeriod = (cutoffPm) => {
  if (!Array.isArray(cutoffPm) || cutoffPm.length === 0) return null

  const validRecords = cutoffPm.filter((record) => {
    if (!record) return false
    // Boolean true o numerico 1 (il BE puo restituire entrambi a seconda della deserializzazione)
    const isSuperCutoff = record.is_super_cutoff === true || record.is_super_cutoff === 1
    if (!isSuperCutoff) return false
    // Escludi solo se is_valid e esplicitamente falsy (0 o false). Se mancante, accetta.
    if (record.is_valid === false || record.is_valid === 0) return false
    if (!record.to_date) return false
    return true
  })

  if (validRecords.length === 0) return null

  // Trova il record con la to_date maggiore senza dipendenze esterne.
  // toISOString-safe: Date(invalid) -> NaN, escluso dal reduce.
  let lastToDate = null
  for (let i = 0; i < validRecords.length; i += 1) {
    const candidate = new Date(validRecords[i].to_date)
    if (isNaN(candidate.getTime())) continue
    if (lastToDate === null || candidate.getTime() > lastToDate.getTime()) {
      lastToDate = candidate
    }
  }

  if (lastToDate === null) return null

  // +1 giorno e normalizza al primo del mese: gestisce correttamente sia i record con
  // to_date a fine mese (es. 2026-02-28 23:59:59 -> 2026-03-01) sia quelli a inizio mese.
  const nextDay = new Date(
    lastToDate.getFullYear(),
    lastToDate.getMonth(),
    lastToDate.getDate() + 1
  )

  const month = nextDay.getMonth() + 1 // 1-based per coerenza con MONTH_OPTIONS
  const year = nextDay.getFullYear()

  // Riusa getMonthDateRange per evitare divergenze di formato/calcolo del fine mese.
  const range = getMonthDateRange(month, year)

  return {
    month: month,
    year: year,
    date_start: range.date_start,
    date_end: range.date_end,
  }
}
